import { selector } from "recoil";

import { SVG_BG } from "@ryde/assets";

import {
  CURRENCY,
  DEFAULT_LOGIN_AUTH_TYPES,
  DEFAULT_SYSTEM_SETTING_SKIP_PREVIEW_STEPS_IF_NO_ERRORS_AT_BOOKINGS,
  DEFAULT_WEEKEND_DURATION_DAYS,
  DEFAULT_WEEKEND_START_DAY,
} from "../../constants";
import { TimeZoneEnum } from "../../enums";
import {
  checkIsPasswordAuthTypes,
  getRideStationOptionsFromSystemParams,
} from "../../helpers";
import { TRideStationOptions } from "../../models";
import { systemSettingsDtoAtom } from "../atoms";

export const currencySignSelector = selector({
  key: "currencySignSelector",
  get: ({ get }) => {
    const systemParams = get(systemSettingsDtoAtom);

    if (!systemParams?.metadata?.currencySign) {
      return CURRENCY.ILS;
    }

    return CURRENCY[systemParams.metadata.currencySign];
  },
});

export const weekendInfoSelector = selector<
  [weekendStart: number, weekendDuration: number]
>({
  key: "weekendInfoSelector",
  get: ({ get }) => {
    const systemParams = get(systemSettingsDtoAtom);
    const weekendStart =
      systemParams?.metadata.weekendStart ?? DEFAULT_WEEKEND_START_DAY;
    const weekendDuration =
      systemParams?.metadata.weekendDuration ?? DEFAULT_WEEKEND_DURATION_DAYS;

    return [weekendStart, weekendDuration];
  },
});

export const timezoneSelector = selector({
  key: "timeZoneSelector",
  get: ({ get }) => {
    const systemParams = get(systemSettingsDtoAtom);
    return systemParams?.metadata?.timeZone || TimeZoneEnum.AsiaJerusalem;
  },
});

export const logoSelector = selector({
  key: "logoSelector",
  get: ({ get }) => {
    const systemParams = get(systemSettingsDtoAtom);

    return systemParams?.metadata?.image_logo || SVG_BG.LogoHeaderMuiSvgBg;
  },
});

export const allowTimeEditInMainPickerSelector = selector({
  key: "allowTimeEditInMainPickerSelector",
  get: ({ get }) => {
    const systemParams = get(systemSettingsDtoAtom);

    return systemParams?.metadata?.allowTimeEditInMainPicker ?? false;
  },
});

export const isPasswordAuthTypeSelector = selector({
  key: "isPasswordAuthTypeSelector",
  get: ({ get }) => {
    const systemParams = get(systemSettingsDtoAtom);

    return checkIsPasswordAuthTypes(
      systemParams?.metadata?.loginSubdomainSettings ??
        DEFAULT_LOGIN_AUTH_TYPES,
    );
  },
});

export const skipPreviewStepsIfNoErrorsSelector = selector<boolean>({
  key: "skipPreviewStepsIfNoErrorsSelector",
  get: ({ get }) => {
    const systemParams = get(systemSettingsDtoAtom);

    return (
      systemParams?.metadata?.skipPreviewStepsIfNoErrors ??
      DEFAULT_SYSTEM_SETTING_SKIP_PREVIEW_STEPS_IF_NO_ERRORS_AT_BOOKINGS
    );
  },
});

export const rideStationOptionsSelector = selector<TRideStationOptions>({
  key: "rideStationOptionsSelector",
  get: ({ get }) => {
    const systemParams = get(systemSettingsDtoAtom);

    return getRideStationOptionsFromSystemParams(systemParams?.metadata);
  },
});

export const stationsForHomeSelector = selector({
  key: "stationsForHomeSelector",
  get: ({ get }) => {
    const systemParams = get(systemSettingsDtoAtom);

    return systemParams?.metadata?.stationsForHome;
  },
});

export const stationsForWorkSelector = selector({
  key: "stationsForWorkSelector",
  get: ({ get }) => {
    const systemParams = get(systemSettingsDtoAtom);

    return systemParams?.metadata?.stationsForWork;
  },
});

export const showPassengerTypeInsteadOfPhoneOnStationsPanelSelector = selector({
  key: "showPassengerTypeInsteadOfPhoneOnStationsPanelSelector",
  get: ({ get }) => {
    const systemParams = get(systemSettingsDtoAtom);

    return systemParams?.metadata
      ?.showPassengerTypeInsteadOfPhoneOnStationsPanel;
  },
});

export const enablePassengerGenderDistinctionSelector = selector({
  key: "enablePassengerGenderDistinctionSelector",
  get: ({ get }) => {
    const { metadata: { enablePassengerGenderDistinction } = {} } =
      get(systemSettingsDtoAtom) ?? {};

    return enablePassengerGenderDistinction ?? false;
  },
});
export const showProduceButtonBookTypeSystemParamSelector = selector({
  key: "showProduceButtonBookTypeSystemParamSelector",
  get: ({ get }) => {
    const systemParams = get(systemSettingsDtoAtom);

    return Boolean(
      systemParams?.metadata?.showProduceButtonBookTypeSystemParam,
    );
  },
});
